import { useEffect } from 'react';
import { useSocket } from '../lib/socket';
import { useWhatsapp } from '../store/whatsapp';
import type {
  WhatsappMessageReceivedPayload,
  WhatsappMessageSentPayload,
  WhatsappMessageStatusPayload,
  WhatsappConversationUpdatedPayload,
} from '@marnic/shared';

export function useWhatsappSocket(): void {
  const socket = useSocket();
  const addInboundMessage   = useWhatsapp((s) => s.addInboundMessage);
  const addOutboundEcho     = useWhatsapp((s) => s.addOutboundEcho);
  const updateMessageStatus = useWhatsapp((s) => s.updateMessageStatus);
  const updateConversation  = useWhatsapp((s) => s.updateConversation);

  useEffect(() => {
    if (!socket) return;

    const onMessage = (p: WhatsappMessageReceivedPayload) => {
      addInboundMessage(p.conversationId, p.message);
    };

    const onSent = (p: WhatsappMessageSentPayload) => {
      addOutboundEcho(p.conversationId, p.message);
    };

    const onStatus = (p: WhatsappMessageStatusPayload) => {
      updateMessageStatus(p.conversationId, p.messageId, {
        status: p.status,
        deliveredAt: p.deliveredAt ?? null,
        readAt: p.readAt ?? null,
        errorCode: p.errorCode ?? null,
      });
    };

    const onConvUpdated = (p: WhatsappConversationUpdatedPayload) => {
      updateConversation(p.conversation);
    };

    socket.on('whatsapp_message_received', onMessage);
    socket.on('whatsapp_message_sent', onSent);
    socket.on('whatsapp_message_status', onStatus);
    socket.on('whatsapp_conversation_updated', onConvUpdated);

    return () => {
      socket.off('whatsapp_message_received', onMessage);
      socket.off('whatsapp_message_sent', onSent);
      socket.off('whatsapp_message_status', onStatus);
      socket.off('whatsapp_conversation_updated', onConvUpdated);
    };
  }, [socket, addInboundMessage, addOutboundEcho, updateMessageStatus, updateConversation]);
}
